/**
 * GL core entry (M1): `createGl(canvas, scale)` builds the engine, scene, main map camera,
 * controller, inset factory and the layer factories, and wires scale changes through them.
 *
 * Per frame the shell calls `gl.frame(dtWall)` (controller + camera animation + tile streaming)
 * after `style.update(fs)`, then `gl.render()`.
 */
import type { RGBA, ScaleState, VectorsFile } from '../api.ts';
import { MapCamera } from './camera.ts';
import { CarLayer, type CarLayerOptions } from './cars.ts';
import { MapController, type ControllerOptions } from './controller.ts';
import { GlEngine, type UniformHub } from './engine.ts';
import { InsetCameraFactory } from './insets.ts';
import { LineFactory } from './lines.ts';
import { parseTileGrid, type TileGridSpec } from './tileGrid.ts';
import { TileCache, TileLayer, type TileLayerOptions } from './tiles.ts';
import { VectorLayers, type VectorTheme } from './vectors.ts';

export { GROUP, HUB_UNIFORMS, LAYER, UniformHub, GlEngine } from './engine.ts';
export { MapCamera, TopDownCamera } from './camera.ts';
export { MapView, type CarPositionSource, type WorldExtent } from './view.ts';
export { MapController, type ControllerInput } from './controller.ts';
export { InsetCameraFactory, type InsetCamera, type InsetSpec } from './insets.ts';
export { TileCache, TileLayer, DEFAULT_GRADE, tileCacheCapacity, TILE_CAPACITY_MIN, TILE_CAPACITY_MAX, type GradeHook, type TileLayerOptions, type TileSampling } from './tiles.ts';
export { VectorLayers, DEFAULT_VECTOR_THEME, type VectorTheme, type VectorKind, type VectorStyle } from './vectors.ts';
export { CarLayer, snapSpriteNdc, CAR_SPRITE_SNAP_GLSL, type CarLayerOptions, type CarLayerMode, type CarAtlasCell, type CarExtraAttr } from './cars.ts';
export { PolyLayer, TrailLayer, LineFactory, type PolyLayerOptions } from './lines.ts';
export { GLSL_HUB, GLSL_STAGE_FRAG } from './shaders/common.ts';
export { GLSL_TILE_SAMPLING, GLSL_TILE_SAMPLING_LINEAR, tileSamplingGlsl, tileFragment } from './shaders/tile.ts';
export { fitPreset, presetFromManifest, crispLevels, crispAtOrAbove, headingToScreen } from './projection.ts';
export { parseTileGrid, type TileGridSpec } from './tileGrid.ts';

export interface GlBundleInput {
  /** bundle base URL (e.g. "/races/2024-monza/v1") */
  baseUrl: string;
  /** base URL for tiles (e.g. "circuits/monza/imagery-1" resolved against data base, or baseUrl) */
  tilesBase?: string | undefined;
  /** vectors.json, when loaded (null/undefined: `gl.vectors` is unavailable, H2) */
  vectors?: VectorsFile | null | undefined;
  /** parsed or raw tiles.json */
  tiles?: unknown;
  vectorTheme?: VectorTheme | undefined;
}

export interface CreateGlOptions {
  clear?: RGBA | undefined;
  controller?: ControllerOptions | undefined;
  /** GL canvas size in device px when it differs from scale.device */
  canvasDevice?: { w: number; h: number } | undefined;
  /** callback for tile load failures */
  onTileError?: ((err: unknown) => void) | undefined;
}

export interface CarLayerFactory {
  create(opts?: CarLayerOptions): CarLayer;
}

export class GlCore {
  readonly gl: GlEngine;
  readonly camera: MapCamera;
  readonly controller: MapController;
  readonly insets: InsetCameraFactory;
  readonly lines: LineFactory;
  readonly cars: CarLayerFactory;
  private grid: TileGridSpec | null = null;
  private baseUrl = '';
  private tileBaseUrl?: string | undefined;
  private readonly caches = new Map<string, TileCache>();
  private readonly cacheList: TileCache[] = [];
  private readonly tileLayers: TileLayer[] = [];
  private vectorLayers: VectorLayers | null = null;
  private vectorData: VectorsFile | null = null;
  private vectorTheme: VectorTheme | undefined;
  private canvasDevice: { w: number; h: number } | undefined;
  private readonly onTileError?: ((err: unknown) => void) | undefined;

  /** The base URL the map's tile layer reads from (`useBundle({ tilesBase })`); undefined before a bundle is set. */
  get tilesBase(): string | undefined {
    return this.tileBaseUrl;
  }

  constructor(canvas: HTMLCanvasElement, scale: ScaleState, opts: CreateGlOptions = {}) {
    this.canvasDevice = opts.canvasDevice;
    this.onTileError = opts.onTileError;
    this.gl = new GlEngine(canvas, scale, opts.clear ? { clear: opts.clear } : {});
    if (opts.canvasDevice) this.gl.applyScale(scale, opts.canvasDevice);
    const eng = this.gl.engine;
    const gle = this.gl;
    const host = {
      scene: this.gl.scene,
      hub: this.gl.hub,
      get pxScale(): number {
        return gle.pxScale;
      },
      renderWidth: () => eng.getRenderWidth(),
      renderHeight: () => eng.getRenderHeight(),
    };
    this.camera = new MapCamera(host);
    this.camera.k = scale.k;
    this.controller = new MapController(this.camera, undefined, opts.controller);
    this.insets = new InsetCameraFactory(host, eng, this.camera.cam);
    this.lines = new LineFactory({ scene: this.gl.scene, hub: this.gl.hub });
    const carHost = { scene: this.gl.scene, hub: this.gl.hub };
    this.cars = { create: (o?: CarLayerOptions) => new CarLayer(carHost, o) };
  }

  get engine(): GlEngine['engine'] {
    return this.gl.engine;
  }
  get scene(): GlEngine['scene'] {
    return this.gl.scene;
  }
  get uniforms(): UniformHub {
    return this.gl.hub;
  }
  get scale(): ScaleState {
    return this.gl.scale;
  }

  /** Bundle resources the layers need (tiles.json grid, vectors.json). */
  useBundle(b: GlBundleInput): void {
    this.baseUrl = b.baseUrl;
    this.tileBaseUrl = b.tilesBase ?? b.baseUrl;
    if (b.tiles) {
      this.grid = isGrid(b.tiles) ? b.tiles : parseTileGrid(b.tiles);
      const g = this.grid;
      this.camera.extent = { x0: g.origin[0], y0: g.origin[1], x1: g.origin[0] + g.extent_m[0], y1: g.origin[1] + g.extent_m[1] };
      this.camera.tileMpps = g.levels.map((l) => l.mpp);
    }
    if (b.vectors) this.vectorData = b.vectors;
    this.vectorTheme = b.vectorTheme;
  }

  /** A tile layer for `grade` (default: the main camera); caches are shared per grade. */
  tiles(grade: string, opts: Partial<TileLayerOptions> = {}): TileLayer {
    const grid = this.grid;
    if (!grid) throw new Error('gl.tiles(): call useBundle({tiles}) first');
    let cache = this.caches.get(grade);
    if (!cache) {
      const tileBase = this.tileBaseUrl ?? this.baseUrl;
      cache = new TileCache(this.gl.engine, grid, tileBase, grade, {
        ...opts,
        // H1 (L11): 16-layer pages (4× cheaper all-page mip regen, identical pixels) and the
        // dynamic capacity rule min(256, max(96, 2 × wanted)), applied grow-only in pump()
        pageLayers: 16,
        tilesBase: tileBase,
        onTileError: this.onTileError,
      });
      cache.attachContextEvents();
      this.cacheList.push(cache);
      this.caches.set(grade, cache);
    }
    const gle = this.gl;
    const layer = new TileLayer(
      { scene: this.gl.scene, engine: this.gl.engine, hub: this.gl.hub, pxScale: () => gle.pxScale },
      cache,
      { ...opts, camera: opts.camera ?? this.camera },
    );
    this.tileLayers.push(layer);
    const dispose = layer.dispose.bind(layer);
    layer.dispose = () => {
      dispose();
      const i = this.tileLayers.indexOf(layer);
      if (i >= 0) this.tileLayers.splice(i, 1);
    };
    return layer;
  }

  /** Vector layers from vectors.json (built on first access). */
  get vectors(): VectorLayers {
    if (!this.vectorLayers) {
      if (!this.vectorData) throw new Error('gl.vectors: call useBundle({vectors}) first');
      this.vectorLayers = new VectorLayers(
        { scene: this.gl.scene, hub: this.gl.hub },
        this.vectorData,
        this.vectorTheme ? { theme: this.vectorTheme } : {},
      );
    }
    return this.vectorLayers;
  }

  /** Apply a new scale state (resize, governor): backbuffer, uPxScale, viewports, crisp levels. */
  applyScale(scale: ScaleState, canvasDevice?: { w: number; h: number }): void {
    if (canvasDevice) this.canvasDevice = canvasDevice;
    this.gl.applyScale(scale, this.canvasDevice);
    this.camera.k = scale.k;
    this.camera.syncCamera();
    this.insets.syncAll();
  }

  /** Per frame (wall-clock dt): controller, camera animation/follow, tile streaming, uTime. */
  frame(dtWall: number, presentationTime?: number): void {
    this.controller.update(dtWall);
    if (presentationTime !== undefined) this.gl.hub.setTime(presentationTime);
    // tiles: every layer collects its wants, each cache pumps once, then layers draw
    const frame = this.gl.engine.frameId;
    const L = this.tileLayers;
    for (let i = 0; i < L.length; i++) L[i]?.collect(frame);
    for (let i = 0; i < this.cacheList.length; i++) this.cacheList[i]?.pump(frame);
    for (let i = 0; i < L.length; i++) L[i]?.draw(frame);
  }

  render(): void {
    this.gl.scene.render();
  }

  dispose(): void {
    for (const l of this.tileLayers.slice()) l.dispose();
    for (const c of this.caches.values()) c.dispose();
    this.caches.clear();
    this.vectorLayers?.dispose();
    this.insets.dispose();
    this.camera.dispose();
    this.gl.dispose();
  }
}

function isGrid(t: unknown): t is TileGridSpec {
  const o = t as Partial<TileGridSpec> | null;
  return !!o && Array.isArray(o.levels) && o.levels.every((l) => typeof l.tile_m === 'number') && typeof o.path === 'string' && Array.isArray(o.grades);
}

/** Build the GL core for a canvas at a scale state. */
export function createGl(canvas: HTMLCanvasElement, scale: ScaleState, opts?: CreateGlOptions): GlCore {
  return new GlCore(canvas, scale, opts);
}
